import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { authApi } from '../api';
import { useLang } from '../contexts/LangContext';

export const ForgotPassword: React.FC = () => {
  const { t, lang, setLang } = useLang();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      await authApi.forgotPassword(email);
      setSuccess(true);
    } catch (err: any) {
      const msg = err?.response?.data?.error?.message || err?.response?.data?.message;
      setError(Array.isArray(msg) ? msg.join(' ') : msg || t('forgotPasswordError'));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:flex-none lg:px-20 xl:px-24">
        <div className="mx-auto w-full max-w-sm lg:w-96">
          <div className="flex justify-end mb-8">
            <div className="flex space-x-2">
              <button
                type="button"
                onClick={() => setLang('vi')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  lang === 'vi' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                VI
              </button>
              <button
                type="button"
                onClick={() => setLang('en')}
                className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                  lang === 'en' ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                EN
              </button>
            </div>
          </div>

          <div>
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">{t('forgotPasswordTitle')}</h2>
            <p className="mt-2 text-sm text-gray-600">{t('forgotPasswordDescription')}</p>
          </div>

          <div className="mt-8">
            {success ? (
              <div className="rounded-xl bg-green-50 border border-green-200 p-4">
                <p className="text-sm text-green-700">{t('forgotPasswordSuccess')}</p>
                <Link to="/login" className="mt-4 inline-block text-sm font-medium text-blue-600 hover:text-blue-500">
                  {t('backToLogin')}
                </Link>
              </div>
            ) : (
              <form className="space-y-6" onSubmit={handleSubmit}>
                {error && (
                  <div className="rounded-xl bg-red-50 border border-red-200 p-4">
                    <div className="text-sm text-red-700">{error}</div>
                  </div>
                )}

                <div>
                  <label htmlFor="email" className="block text-sm font-semibold text-gray-700 mb-2">
                    {t('email')}
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    autoComplete="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="block w-full px-4 py-3 rounded-xl border border-gray-200 shadow-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none transition-all duration-200 placeholder-gray-400"
                    placeholder={t('enterEmail')}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isLoading}
                  className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-semibold text-white bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? t('sendingResetLink') : t('sendResetLink')}
                </button>

                <div className="text-center">
                  <Link to="/login" className="text-sm font-medium text-blue-600 hover:text-blue-500">
                    {t('backToLogin')}
                  </Link>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>

      <div className="hidden lg:block relative w-0 flex-1">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-indigo-600 to-purple-700" />
      </div>
    </div>
  );
};
